import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildChordString,
  ChordObjectModelV3,
  ChordQuality,
  Note,
  NATURAL_NOTES,
  MODIFIERS,
  chordQualities,
  chordExtensions,
  chordAdds,
  chordAlterations,
  getDisabledAdds,
  getDisabledAlterations,
  getDisabledQualities,
  getDisabledExtensions,
  getDefaultExtensionForQuality
} from '@chordbaker/music-theory';
import { useTheme } from '../contexts/ThemeContext';
import CustomChordInput from './CustomChordInput';
import CloseIcon from './icons/CloseIcon';
import BeatsIcon from './icons/BeatsIcon';
import SilenceIcon from './icons/SilenceIcon';
import ModeToggle from './ModeToggle';
import { 
  ModifierType, 
  buildNote,
  extractModifier
} from '../utils/noteHelpers';

// Map imported data to match existing variable names
const qualities = chordQualities;
const extensions = chordExtensions;
const addsData = chordAdds;
const alterationsDataFile = chordAlterations;

type ChordEditorData = { chord?: ChordObjectModelV3; customText?: string };

interface ChordEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (data: ChordEditorData) => void;
  onClear: () => void;
  initialChordData?: ChordEditorData | null;
  /** The "Make rest" shortcut (empties the slot). */
  onMakeRest?: () => void;
  /**
   * The Beats shortcut. Gets the edited chord to apply first, or null when
   * nothing was touched (an empty slot's editor shows C, which must not land
   * on the slot just because Beats was clicked).
   */
  onOpenBeats?: (edits: ChordEditorData | null) => void;
  /** The Beats button's label, e.g. "Beats · 2/4" (just "Beats" without a meter). */
  beatsLabel?: string;
}

const ChordEditorModal: React.FC<ChordEditorModalProps> = ({
  isOpen,
  onClose,
  onApply,
  onClear,
  initialChordData,
  onMakeRest,
  onOpenBeats,
  beatsLabel = 'Beats',
}) => {
  const { theme } = useTheme(); // Add theme hook
  
  // Mode state
  const [mode, setMode] = useState<'builder' | 'custom'>('builder');
  const [customText, setCustomText] = useState<string>('');

  // Debug logging for mode changes
  useEffect(() => {
    console.log('[ChordEditorModal] Mode changed to:', mode);
  }, [mode]);

  // New state for separate note and modifier selection
  const [selectedRootNote, setSelectedRootNote] = useState<string>("C");
  const [selectedRootModifier, setSelectedRootModifier] = useState<ModifierType>('natural');
  const [selectedBassNote, setSelectedBassNote] = useState<string>("C");
  const [selectedBassModifier, setSelectedBassModifier] = useState<ModifierType>('natural');
  const [isDominant, setIsDominant] = useState<boolean>(false);
  const [selectedQuality, setSelectedQuality] = useState<string>("major");
  const [selectedExtension, setSelectedExtension] = useState<string>("-");
  const [selectedAdds, setSelectedAdds] = useState<string[]>(["-"]);
  const [selectedAlterations, setSelectedAlterations] = useState<string[]>(["-"]);

  const [disabledAddOptions, setDisabledAddOptions] = useState<Set<string>>(new Set());
  const prevFundamentalRef = useRef<string | null | undefined>(undefined);
  const justOpenedRef = useRef(false);
  const hasInitializedRef = useRef(false); // Track if we've initialized for this modal session
  // Whether the user changed anything since the editor opened (see onOpenBeats)
  const isEditedRef = useRef(false);

  // Track previous root note and modifier for change detection
  const prevRootNoteRef = useRef<string | undefined>(undefined);
  const prevRootModifierRef = useRef<ModifierType | undefined>(undefined);

  // Computed root note display string from root note + modifier
  const selectedFundamental = useMemo(() => {
    if (selectedRootModifier === 'natural') {
      return selectedRootNote;
    }
    return selectedRootNote + (selectedRootModifier === 'flat' ? '♭' : '♯');
  }, [selectedRootNote, selectedRootModifier]);

  // Computed bass note from bass natural note + modifier
  const selectedBass = useMemo(() => {
    let bass = selectedBassNote;
    if (selectedBassModifier === 'flat') {
      bass += '♭';
    } else if (selectedBassModifier === 'sharp') {
      bass += '♯';
    }
    return bass;
  }, [selectedBassNote, selectedBassModifier]);

  useEffect(() => {
    console.log('[ChordEditorModal] useEffect triggered - isOpen:', isOpen, 'initialChordData:', initialChordData, 'hasInitialized:', hasInitializedRef.current);
    
    if (isOpen && !hasInitializedRef.current) {
      // Only initialize once when modal opens
      hasInitializedRef.current = true;
      isEditedRef.current = false;
      justOpenedRef.current = true;
      
      // Check if initialChordData has customText
      if (initialChordData?.customText) {
        // Custom mode
        console.log('[ChordEditorModal] useEffect: Setting mode to custom (has customText)');
        setMode('custom');
        setCustomText(initialChordData.customText);
        
        // Reset builder state to defaults
        setSelectedRootNote("C");
        setSelectedRootModifier('natural');
        setSelectedBassNote("C");
        setSelectedBassModifier('natural');
        setSelectedQuality("major");
        setSelectedAdds(["-"]);
        setSelectedAlterations(["-"]);
        setSelectedExtension("-");
        setIsDominant(false);
      } else if (initialChordData?.chord && initialChordData.chord.root) {
        // Builder mode with existing chord
        console.log('[ChordEditorModal] useEffect: Setting mode to builder (has chord data)');
        setMode('builder');
        setCustomText('');

        // Extract note and modifier from V3 Note object
        const root = initialChordData.chord.root;
        setSelectedRootNote(root.noteName);
        setSelectedRootModifier(extractModifier(root));

        // Extract bass note and modifier from V3 Note object
        const bass = initialChordData.chord.bass;
        if (bass) {
          setSelectedBassNote(bass.noteName);
          setSelectedBassModifier(extractModifier(bass));
        } else {
          // Default bass to root
          setSelectedBassNote(root.noteName);
          setSelectedBassModifier(extractModifier(root));
        }
        
        setIsDominant(initialChordData.chord.isDominant ?? false);
        setSelectedQuality(initialChordData.chord.quality ?? "major");
        setSelectedExtension(initialChordData.chord.extension ?? "-");
        setSelectedAdds(initialChordData.chord.adds?.length ? initialChordData.chord.adds : ["-"]);
        if (initialChordData.chord.alterations && initialChordData.chord.alterations.includes("alt")) {
          setSelectedAlterations([]);
        } else {
          setSelectedAlterations(initialChordData.chord.alterations?.length ? initialChordData.chord.alterations : ["-"]);
        }
      } else {
        // New chord - default to builder mode
        console.log('[ChordEditorModal] useEffect: Setting mode to builder (new chord/no data)');
        setMode('builder');
        setCustomText('');
        setSelectedRootNote("C");
        setSelectedRootModifier('natural');
        setSelectedBassNote("C");
        setSelectedBassModifier('natural');
        setSelectedQuality("major");
        setSelectedAdds(["-"]);
        setSelectedAlterations(["-"]);
        setSelectedExtension("-");
        setIsDominant(false);
      }
      queueMicrotask(() => {
        justOpenedRef.current = false;
      });
    } else if (!isOpen) {
      // Reset initialization flag when modal closes
      hasInitializedRef.current = false;
    }
  }, [isOpen]); // Only depend on isOpen - initialChordData is captured when modal opens

  useEffect(() => {
    if (isDominant) {
      const disabledQualities = getDisabledQualities(true);
      if (disabledQualities.has(selectedQuality || "")) {
        setSelectedQuality("major");
      }
      // When becoming dominant, if extension is invalid ("-" or "6"), set to "7"
      if (selectedExtension === "" || selectedExtension === "-" || selectedExtension === "6") {
        setSelectedExtension("7");
      }
    }
  }, [isDominant, selectedQuality, selectedExtension]);

  useEffect(() => {
    if ((selectedQuality === "sus2" || selectedQuality === "sus4") && !isDominant) {
      if (selectedExtension !== "-") {
        setSelectedExtension("-");
      }
    }
  }, [selectedQuality, isDominant, selectedExtension]);

   useEffect(() => {
    const isSus = selectedQuality === "sus2" || selectedQuality === "sus4";
    if (isDominant && isSus) {
      if (selectedExtension === "11" || selectedExtension === "13") {
        setSelectedExtension("7");
      }
    }
    if (isDominant && selectedQuality === "sus2") {
      if (selectedExtension === "9") {
        setSelectedExtension("7");
      }
    }
  }, [isDominant, selectedQuality, selectedExtension]);

  const getQualityOptionDisabledState = (optionValue: string, currentIsDominant: boolean): boolean => {
    const disabledQualities = getDisabledQualities(currentIsDominant);
    return disabledQualities.has(optionValue);
  };

  const getAlterationOptionDisabledState = (alterationValue: string): boolean => {
    const disabledAlterations = getDisabledAlterations(
      selectedQuality,
      selectedExtension,
      isDominant,
      selectedAlterations.filter(a => a !== "-")
    );
    return disabledAlterations.has(alterationValue);
  };

  const getExtensionOptionDisabledState = (optionValue: string, currentIsDominant: boolean, currentIsSusChordSelected: boolean): boolean => {
    const disabledExtensions = getDisabledExtensions(selectedQuality, currentIsDominant);
    return disabledExtensions.has(optionValue);
  };

  useEffect(() => {
    // Handle interactions when selectedQuality changes
    // Check if current extension is valid for the selected quality
    const defaultExtension = getDefaultExtensionForQuality(selectedQuality, selectedExtension, isDominant);
    if (defaultExtension !== null) {
      setSelectedExtension(defaultExtension);
    }
    // Note: The existing useEffect for isDominant will also clear selectedQuality if it becomes incompatible.
  }, [selectedQuality, selectedExtension, isDominant]); // Include isDominant for proper validation

  useEffect(() => {
    if (!isOpen) {
      prevRootNoteRef.current = undefined;
      prevRootModifierRef.current = undefined;
      return;
    }

    // Skip on initial open
    if (justOpenedRef.current) {
      prevRootNoteRef.current = selectedRootNote;
      prevRootModifierRef.current = selectedRootModifier;
      return;
    }

    // Check if root note or root modifier changed
    const rootNoteChanged = prevRootNoteRef.current !== undefined && 
                            prevRootNoteRef.current !== selectedRootNote;
    const rootModifierChanged = prevRootModifierRef.current !== undefined && 
                                prevRootModifierRef.current !== selectedRootModifier;

    if (rootNoteChanged || rootModifierChanged) {
      // Root changed - reset bass to match root
      setSelectedBassNote(selectedRootNote);
      setSelectedBassModifier(selectedRootModifier);
    }

    // Update refs
    prevRootNoteRef.current = selectedRootNote;
    prevRootModifierRef.current = selectedRootModifier;
  }, [isOpen, selectedRootNote, selectedRootModifier]);

  useEffect(() => {
    if (!selectedFundamental) { 
      setDisabledAddOptions(new Set()); 
      return; 
    }
    
    const disabledAdds = getDisabledAdds(
      selectedFundamental,
      selectedQuality,
      selectedExtension,
      isDominant,
      selectedAlterations.filter(a => a !== "-")
    );
    
    setDisabledAddOptions(disabledAdds);
    // Note: We only mark adds as disabled, we don't remove them from selectedAdds
    // This matches React Native behavior and prevents data loss when editing chords
  }, [selectedFundamental, selectedQuality, selectedExtension, isDominant, selectedAlterations]);

  useEffect(() => {
    const activeAlterations = selectedAlterations.filter(alt => alt !== "-");
    if (activeAlterations.length === 0) return;
    const validSelectedAlterations = activeAlterations.filter(alt => !getAlterationOptionDisabledState(alt));
    if (validSelectedAlterations.length !== activeAlterations.length) {
      setSelectedAlterations(validSelectedAlterations.length > 0 ? validSelectedAlterations : ["-"]);
    }
  }, [selectedQuality, selectedExtension, isDominant, selectedAlterations]);

  // Effect for Enharmonic Chord Equivalents -- REMOVED (this was for the old "Interpret As" feature)
  // useEffect(() => {
  //   if (!isOpen || !selectedFundamental || !selectedQuality ) {
  //     setEnharmonicChordOptions([]);
  //     return;
  //   }
  //   const currentParsedChord: ParsedChordData = {
  //     fundamental: selectedFundamental, quality: selectedQuality,
  //     extension: selectedExtension === "-" ? null : selectedExtension,
  //     isDominant: isDominant, adds: selectedAdds.filter(a => a !== "-"),
  //     alterations: selectedAlterations.filter(a => a !== "-"), bass: selectedBass
  //   };
  //   // const alternatives = getEnharmonicChordAlternatives(currentParsedChord); // Old system
  //   // setEnharmonicChordOptions(alternatives);
  // }, [isOpen, selectedFundamental, selectedQuality, selectedExtension, isDominant, selectedAdds, selectedAlterations, selectedBass]);

  // const handleEnharmonicInterpretationSelect = (interpretation: ParsedChordData) => { // REMOVED
  //   if (interpretation.fundamental) setSelectedFundamental(interpretation.fundamental);
  //   if (interpretation.quality) setSelectedQuality(interpretation.quality);
  //   setSelectedExtension(interpretation.extension ?? "-");
  //   setIsDominant(interpretation.isDominant);
  //   setSelectedAdds(interpretation.adds.length > 0 ? interpretation.adds : ["-"]);
  
  const handleCustomButtonPress = () => {
    isEditedRef.current = true;
    if (mode === 'custom') {
      // Switch back to builder mode
      console.log('[ChordEditorModal] handleCustomButtonPress: switching from custom to builder');
      setMode('builder');
      setCustomText('');
    } else {
      // Switch to custom mode
      console.log('[ChordEditorModal] handleCustomButtonPress: switching from builder to custom');
      setMode('custom');
      // Keep existing customText if any
    }
  };

  const handleBuilderInteraction = () => {
    isEditedRef.current = true;
    if (mode === 'custom') {
      console.log('[ChordEditorModal] handleBuilderInteraction: AUTO-SWITCHING from custom to builder');
      console.trace('[ChordEditorModal] Stack trace for auto-switch:');
      setMode('builder');
      setCustomText('');
    }
  };

  const handleClear = () => {
    isEditedRef.current = true;
    // Reset bass to match current root
    setSelectedBassNote(selectedRootNote);
    setSelectedBassModifier(selectedRootModifier);

    // Reset other builder state to defaults (keep root and modifier)
    setSelectedQuality("major");
    setSelectedAdds(["-"]);
    setSelectedAlterations(["-"]);
    setSelectedExtension("-");
    setIsDominant(false);
  };

  // What Apply would write: custom text, a built chord, or nothing (empty slot)
  const buildApplyData = (): ChordEditorData => {
    if (mode === 'custom') {
      const trimmedText = customText.trim();
      // Empty custom text - treat as clearing the slot
      return trimmedText.length === 0
        ? { customText: undefined, chord: undefined }
        : { customText: trimmedText, chord: undefined };
    }
    // Builder mode - build V3 ChordObjectModelV3 with Note objects
    const chordData: ChordObjectModelV3 = {
      root: buildNote(selectedRootNote as any, selectedRootModifier),
      isDominant: isDominant,
      quality: (selectedQuality === "-" ? null : selectedQuality) as ChordQuality | null,
      bass: buildNote(selectedBassNote as any, selectedBassModifier),
      extension: selectedExtension === "-" ? null : selectedExtension,
      adds: selectedAdds.filter(add => add !== "-"),
      alterations: selectedAlterations.filter(alt => alt !== "-"),
    };
    return { chord: chordData, customText: undefined };
  };

  const handleApply = () => {
    onApply(buildApplyData());
  };

  const handleOpenBeats = () => {
    onOpenBeats?.(isEditedRef.current ? buildApplyData() : null);
  };

  const handleCustomTextChange = (text: string) => {
    isEditedRef.current = true;
    setCustomText(text);
  };

  const renderButtonGroup = (
    labelParam: string,
    selectedValue: any,
    setter: (value: any) => void,
    options: Array<{ value: string, display: string } | string>,
    allowNull: boolean = true,
    gridColsClass: string = 'grid-cols-6',
    disabled: ((optionValue: string) => boolean) | boolean = false,
    isMultiSelect: boolean = false
  ) => {
    const isGroupDisabled = typeof disabled === 'boolean' && disabled;
    return (
      <div className={`mb-6 ${isGroupDisabled ? 'opacity-50' : ''}`}>
        <label 
          className="block text-sm font-medium mb-2"
          style={{ color: theme.colors.text }}
        >
          {labelParam}
        </label>
        <div className={`grid ${gridColsClass} gap-1.5`}>
          {options.map(option => {
            const value = typeof option === 'string' ? option : option.value;
            const display = typeof option === 'string' ? option : option.display;
            const isSelected = isMultiSelect
              ? Array.isArray(selectedValue) && selectedValue.includes(value)
              : selectedValue === value;

            let buttonDisabled = false;
            if (typeof disabled === 'boolean') {
              buttonDisabled = disabled;
            } else if (typeof disabled === 'function') {
              buttonDisabled = disabled(value);
            }

            return (
              <button
                key={value}
                disabled={buttonDisabled}
                onClick={() => {
                  if (buttonDisabled && !isSelected && (isMultiSelect || value !== selectedValue)) return;
                  if (buttonDisabled && isSelected && labelParam === "Bass") return;

                  // Switch to builder mode when any builder button is clicked
                  handleBuilderInteraction();

                  if (isMultiSelect) {
                    const currentArray = Array.isArray(selectedValue) ? selectedValue : [];
                    let newArray;
                    if (value === "-") {
                      newArray = ["-"];
                    } else {
                      if (currentArray.includes(value)) {
                        newArray = currentArray.filter((item: string) => item !== value);
                        if (newArray.length === 0) newArray = ["-"];
                      } else {
                        newArray = [...currentArray.filter((item: string) => item !== "-"), value];
                      }
                    }
                    setter(newArray);
                  } else {
                    if (labelParam === "Bass") {
                      if (!buttonDisabled) setter(value);
                    } else {
                      const hasDashOption = options.some(opt => (typeof opt === 'object' ? opt.value : opt) === "-");
                      if (hasDashOption && allowNull) {
                        if (isSelected && value === "-") setter("-");
                        else if (isSelected) setter("-");
                        else setter(value);
                      } else {
                        if (allowNull && isSelected) setter(null);
                        else setter(value);
                      }
                    }
                  }
                }}
                className="w-full py-2 px-1 text-xs rounded transition-colors flex items-center justify-center h-8 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-inset"
                style={{
                  backgroundColor: isSelected && !buttonDisabled
                    ? theme.colors.primary
                    : theme.colors.surfaceSecondary,
                  color: isSelected && !buttonDisabled
                    ? theme.colors.textInverse
                    : theme.colors.text,
                  border: 'none',
                  opacity: buttonDisabled ? theme.opacity.disabled : 1,
                  cursor: buttonDisabled ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!buttonDisabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!buttonDisabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                  }
                }}
                title={display}
              >
                <span className="truncate">{display}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const isSusChordSelected = selectedQuality === "sus2" || selectedQuality === "sus4";

  // Check if reset is needed (are any values different from defaults?)
  // The slot shortcut buttons: outlined pills
  const slotActionStyle: React.CSSProperties = {
    minHeight: 44,
    borderRadius: theme.radii.full,
    border: `1px solid ${theme.colors.borderSecondary}`,
    backgroundColor: 'transparent',
    color: theme.colors.text,
    cursor: 'pointer',
  };

  const isResetNeeded = useMemo(() => {
    const bassMatchesRoot = selectedBassNote === selectedRootNote && selectedBassModifier === selectedRootModifier;
    const addsAreDefault = selectedAdds.length === 1 && selectedAdds[0] === "-";
    const alterationsAreDefault = selectedAlterations.length === 1 && selectedAlterations[0] === "-";

    return !(
      bassMatchesRoot &&
      selectedQuality === "major" &&
      selectedExtension === "-" &&
      addsAreDefault &&
      alterationsAreDefault &&
      !isDominant
    );
  }, [selectedBassNote, selectedBassModifier, selectedRootNote, selectedRootModifier, selectedQuality, selectedExtension, selectedAdds, selectedAlterations, isDominant]);

  // Build chord display string
  const chordDisplayString = useMemo(() => {
    if (mode === 'custom') {
      return customText || '- - -';
    }
    
    // Build V3 chord object for display
    const chordForDisplay: ChordObjectModelV3 = {
      root: buildNote(selectedRootNote as any, selectedRootModifier),
      quality: (selectedQuality === "-" ? null : selectedQuality) as ChordQuality | null,
      extension: selectedExtension === "-" ? null : selectedExtension,
      isDominant: isDominant,
      adds: selectedAdds.filter(add => add !== "-"),
      alterations: selectedAlterations.filter(alt => alt !== "-"),
      bass: buildNote(selectedBassNote as any, selectedBassModifier),
    };
    
    return buildChordString(chordForDisplay);
  }, [mode, customText, selectedRootNote, selectedRootModifier, selectedQuality, selectedExtension, isDominant, selectedAdds, selectedAlterations, selectedBassNote, selectedBassModifier]);

  // Early return after all hooks are declared (Rules of Hooks compliance)
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 overflow-y-auto h-full w-full flex justify-center items-center p-4 z-50"
      style={{ backgroundColor: theme.colors.modalOverlay }}
    >
      <div 
        className="p-6 rounded-lg w-full max-w-xl max-h-[90vh] flex flex-col"
        style={{
          backgroundColor: theme.colors.modalBackground,
          boxShadow: theme.shadows.modal,
        }}
      >
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h2 
            className="text-2xl font-semibold"
            style={{ color: theme.colors.text }}
          >
            Select chord
          </h2>
          <button 
            onClick={onClose} 
            className="hover:opacity-70 transition-opacity" 
            title="Close"
            style={{ color: theme.colors.textSecondary }}
          >
            <CloseIcon size={24} color="currentColor" />
          </button>
        </div>
        <div className="mb-6 h-12 flex justify-center flex-shrink-0">
          {/* Read-only preview: bordered (buttons in the app are flat) and unshadowed */}
          <div 
            className="rounded-md flex items-center justify-center px-6 min-w-[80px] h-full"
            style={{
              backgroundColor: theme.isDark ? theme.colors.surfaceSecondary : theme.colors.surface,
              border: `1px solid ${theme.colors.border}`,
            }}
          >
            <span 
              className="text-2xl tracking-wider"
              style={{ color: theme.colors.text }}
            >
              {chordDisplayString}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 flex-grow overflow-y-auto pr-2">
          {mode === 'custom' ? (
            // Custom mode - show custom text input
            <div className="col-span-1 md:col-span-2">
              <CustomChordInput
                value={customText}
                onChange={handleCustomTextChange}
                theme={theme}
              />
            </div>
          ) : (
            // Builder mode - show chord builder UI
            <>
              <div className="flex flex-col">
                {renderButtonGroup("Root", selectedRootNote, (val) => { handleBuilderInteraction(); setSelectedRootNote(val); }, [...NATURAL_NOTES], false, 'grid-cols-4 sm:grid-cols-7')}
                {renderButtonGroup("Modifier", selectedRootModifier, (val) => { handleBuilderInteraction(); setSelectedRootModifier(val); }, [
                  { value: 'natural', display: '-' },
                  { value: 'flat', display: '♭' },
                  { value: 'sharp', display: '♯' }
                ], false, 'grid-cols-3 sm:grid-cols-3')}

                <div className="mb-6 flex items-center gap-3">
                  <span
                    className="text-sm font-medium"
                    style={{ color: theme.colors.text }}
                  >
                    Dominant
                  </span>
                  <button
                    onClick={() => { handleBuilderInteraction(); setIsDominant(!isDominant); }}
                    style={{
                      width: '44px',
                      height: `${theme.spacing.xl}px`,
                      borderRadius: `${theme.radii.full}px`,
                      padding: '3px',
                      position: 'relative',
                      backgroundColor: isDominant ? theme.colors.primary : theme.colors.textSecondary,
                      border: 'none',
                      cursor: 'pointer',
                      transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: `${theme.radii.full}px`,
                        backgroundColor: theme.colors.background,
                        position: 'absolute',
                        left: isDominant ? 'calc(100% - 21px)' : '3px',
                        top: '3px',
                        transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
                      }}
                    />
                  </button>
                </div>

                {renderButtonGroup("Quality", selectedQuality, setSelectedQuality, qualities, false, 'grid-cols-3 sm:grid-cols-4', (val) => getQualityOptionDisabledState(val, isDominant))}
                {renderButtonGroup("Extension", selectedExtension, setSelectedExtension, extensions, true, 'grid-cols-3 sm:grid-cols-4', (val) => getExtensionOptionDisabledState(val, isDominant, isSusChordSelected))}
              </div>
              <div className="flex flex-col">
                {renderButtonGroup("Adds", selectedAdds, setSelectedAdds, addsData, true, 'grid-cols-3 sm:grid-cols-4', (val) => disabledAddOptions.has(val), true)}
                {renderButtonGroup("Alterations", selectedAlterations, setSelectedAlterations, alterationsDataFile, true, 'grid-cols-3 sm:grid-cols-4', getAlterationOptionDisabledState, true)}
                {renderButtonGroup("Bass", selectedBassNote, (val) => { handleBuilderInteraction(); setSelectedBassNote(val); }, [...NATURAL_NOTES], false, 'grid-cols-4 sm:grid-cols-7')}
                {renderButtonGroup("Bass Modifier", selectedBassModifier, (val) => { handleBuilderInteraction(); setSelectedBassModifier(val); }, [
                  { value: 'natural', display: '-' },
                  { value: 'flat', display: '♭' },
                  { value: 'sharp', display: '♯' }
                ], false, 'grid-cols-3 sm:grid-cols-3')}
              </div>
            </>
          )}
        </div>
        {/* Slot shortcuts: make the slot a rest, or edit its beats */}
        {(onMakeRest || onOpenBeats) && (
          <div
            className="flex gap-3 mt-4 pt-4 border-t flex-shrink-0"
            style={{ borderColor: theme.colors.border }}
          >
            {onMakeRest && (
              <button
                onClick={onMakeRest}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium"
                style={slotActionStyle}
                data-testid="chord-editor-make-rest"
              >
                <SilenceIcon size={theme.sizes.icon.md} color={theme.colors.text} />
                Make rest
              </button>
            )}
            {onOpenBeats && (
              <button
                onClick={handleOpenBeats}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium"
                style={slotActionStyle}
                data-testid="chord-editor-beats"
              >
                <BeatsIcon size={theme.sizes.icon.md} color={theme.colors.text} />
                {beatsLabel}
              </button>
            )}
          </div>
        )}
        <div
          className="flex justify-end items-center space-x-4 mt-4 pt-4 border-t flex-shrink-0"
          style={{ borderColor: theme.colors.border }}
        >
          <ModeToggle
            mode={mode}
            onToggle={handleCustomButtonPress}
          />
          {mode === 'builder' && (
            <button
              onClick={handleClear}
              disabled={!isResetNeeded}
              className="px-6 py-2 text-sm font-medium rounded-md transition-colors"
              style={{
                backgroundColor: theme.colors.surfaceSecondary,
                color: theme.colors.text,
                border: 'none',
                opacity: isResetNeeded ? 1 : theme.opacity.disabled,
                cursor: isResetNeeded ? 'pointer' : 'not-allowed',
              }}
              onMouseEnter={(e) => {
                if (isResetNeeded) {
                  e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                }
              }}
              onMouseLeave={(e) => {
                if (isResetNeeded) {
                  e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                }
              }}
            >
              Reset
            </button>
          )}
          <button
            onClick={handleApply}
            className="px-6 py-2 text-sm font-medium rounded-md transition-colors"
            style={{
              backgroundColor: theme.colors.primary,
              border: 'none',
              color: theme.colors.textInverse,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChordEditorModal;
